import React, { useState, useEffect } from 'react';
import { Search, X, ChevronDown, ChevronUp, Calendar } from 'lucide-react';

interface SearchFormProps {
  onSearch: (params: {
    start_date?: string;
    end_date?: string;
    customer_name?: string;
    bank_name?: string;
  }) => void;
  loading?: boolean;
}

const SearchForm: React.FC<SearchFormProps> = ({ onSearch, loading = false }) => {
  // 取得今天日期的 YYYY-MM-DD 格式
  const getTodayFormatted = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 取得15天前的日期
  const getFifteenDaysAgoFormatted = () => {
    const today = new Date();
    const fifteenDaysAgo = new Date(today.getTime() - (15 * 24 * 60 * 60 * 1000));
    const year = fifteenDaysAgo.getFullYear();
    const month = String(fifteenDaysAgo.getMonth() + 1).padStart(2, '0');
    const day = String(fifteenDaysAgo.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(getFifteenDaysAgoFormatted());
  const [endDate, setEndDate] = useState(getTodayFormatted());
  const [customerName, setCustomerName] = useState('');
  const [bankName, setBankName] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // 組件載入時自動執行一次搜尋（帶入最近15天的日期區間）
  useEffect(() => {
    if (!hasInitialized) {
      onSearch({
        start_date: getFifteenDaysAgoFormatted(),
        end_date: getTodayFormatted()
      });
      setHasInitialized(true);
    }
  }, [hasInitialized]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch({
      start_date: startDate.trim() || undefined,
      end_date: endDate.trim() || undefined,
      customer_name: customerName.trim() || undefined,
      bank_name: bankName.trim() || undefined,
    });
  };

  const handleReset = () => {
    setStartDate(getFifteenDaysAgoFormatted());
    setEndDate(getTodayFormatted());
    setCustomerName('');
    setBankName('');
    onSearch({
      start_date: getFifteenDaysAgoFormatted(),
      end_date: getTodayFormatted()
    });
  };

  const hasSearchValue = startDate || endDate || customerName || bankName;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* 手機版摺疊標題 */}
      <div className="sm:hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center">
            <Search className="h-4 w-4 text-blue-600 mr-2" />
            <span className="text-base font-semibold text-gray-800">搜尋匯款記錄</span>
            <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
              莊家羚
            </span>
            {hasSearchValue && (
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                已設條件
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-600" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-600" />
          )}
        </button>
      </div>

      {/* 桌面版標題 */}
      <div className="hidden sm:block p-4 sm:p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Search className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-2" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-800">搜尋匯款記錄</h2>
          </div>
          <div className="flex items-center">
            <span className="text-sm text-gray-500 mr-2">業務員:</span>
            <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full font-medium">
              莊家羚
            </span>
          </div>
        </div>
      </div>

      {/* 搜尋表單內容 */}
      <div className={`${isExpanded ? 'block' : 'hidden'} sm:block`}>
        <form onSubmit={handleSubmit} className="p-4 sm:p-6">
          <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 mb-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="h-4 w-4 inline mr-1" />
                開始日期
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="h-4 w-4 inline mr-1" />
                結束日期
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-1">
                客戶名稱
              </label>
              <input
                type="text"
                id="customerName"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="請輸入客戶名稱"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
              />
            </div>
            
            <div>
              <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-1">
                銀行名稱
              </label>
              <input
                type="text"
                id="bankName"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="請輸入銀行名稱"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-base sm:text-sm font-medium"
            >
              <Search className="h-4 w-4 mr-2" />
              {loading ? '搜尋中...' : '搜尋'}
            </button>
            
            <button
              type="button"
              onClick={handleReset}
              className="flex-1 sm:flex-none px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center text-base sm:text-sm font-medium"
            >
              <X className="h-4 w-4 mr-2" />
              重置
            </button>
          </div>
          
          <div className="mt-3 text-xs text-gray-500">
            <p>* 系統預設查詢最近15天的記錄，固定業務員為「莊家羚」</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SearchForm; 